/* eslint-disable no-underscore-dangle */

import Pusher, { Channel } from 'pusher-js'
import request from '@/axios/config'

const pusherAppKey = import.meta.env.VITE_PUSHER_APP_KEY
const pusherCluster = import.meta.env.VITE_PUSHER_CLUSTER

// Check if Pusher is configured - if not, service will work in offline mode
const isPusherConfigured = !!(pusherAppKey && pusherCluster)

if (!isPusherConfigured) {
  // eslint-disable-next-line no-console
  console.warn(
    'Pusher configuration missing (VITE_PUSHER_APP_KEY or VITE_PUSHER_CLUSTER). ' +
      'NotificationCenter will work without real-time updates.'
  )
}

export enum PusherConnectionEvent {
  CONNECTED = 'connected',
}

export enum PusherSubscriptionEvent {
  SUBSCRIPTION_SUCCEEDED = 'pusher:subscription_succeeded',
  SUBSCRIPTION_ERROR = 'pusher:subscription_error',
}

export enum NotificationEvent {
  CREATED = 'notification.created',
  READ = 'notification.read',
  UNREAD_COUNT = 'notification.unread_count',
}

export interface NotificationData {
  id: string
  type: string
  title: string
  content: string
  metadata?: Record<string, unknown>
  created_at: string | Date // Pusher sends as string, but allow Date for flexibility
}

export interface NotificationReadData {
  id: string
  status: 'read'
  read_at: string | Date // Pusher sends as string, but allow Date for flexibility
}

export interface UnreadCountData {
  unread_count: number
  updated_at: string | Date // Pusher sends as string, but allow Date for flexibility
}

export type NotificationEventHandler = (data: NotificationData) => void
export type NotificationReadHandler = (data: NotificationReadData) => void
export type UnreadCountHandler = (data: UnreadCountData) => void

const authEndpoint = 'v1/notification-center/pusher/auth'

export class NotificationCenterService {
  private userId: string

  private pusher: Pusher | null = null

  private channel: Channel | null = null

  private channelName: string

  private _isReady = false

  private _isPusherEnabled = isPusherConfigured

  private notificationHandlers: NotificationEventHandler[] = []

  private readHandlers: NotificationReadHandler[] = []

  private unreadCountHandlers: UnreadCountHandler[] = []

  private readyHandlers: (() => void)[] = []

  get isReady() {
    return this._isReady
  }

  /**
   * @param userId This MUST be the user's Directus user ID
   */
  constructor(userId: string) {
    this.userId = userId
    this.channelName = `private-user.${this.userId}`

    if (this._isPusherEnabled) {
      this.pusher = new Pusher(pusherAppKey as string, {
        cluster: pusherCluster as string,
        authorizer: channel => ({
          authorize: this.createAuthorizer(channel),
        }),
      })

      this.pusher.connection.bind(
        PusherConnectionEvent.CONNECTED,
        this.handleConnected
      )
    } else {
      // In offline mode, mark as ready immediately
      setTimeout(() => {
        this._isReady = true
        this.readyHandlers.forEach(handler => handler())
      }, 0)
    }
  }

  private createAuthorizer =
    (channel: Channel) =>
    async (
      socketId: string,
      callback: (error: Error | null, authData: { auth: string } | null) => void
    ) => {
      try {
        const {
          data: { auth },
        } = await request<{ auth: string }>({
          url: authEndpoint,
          method: 'POST',
          data: {
            channel_name: channel.name,
            socket_id: socketId,
          },
        })

        callback(null, { auth })
      } catch (error) {
        callback(
          error instanceof Error ? error : new Error(String(error)),
          null
        )
      }
    }

  private handleConnected = () => {
    if (!this.pusher) return

    // Subscribe to the private channel for this user
    this.channel = this.pusher.subscribe(this.channelName)

    // Bind to channel events
    this.channel.bind(
      PusherSubscriptionEvent.SUBSCRIPTION_SUCCEEDED,
      this.handleSubscriptionSucceeded
    )
    this.channel.bind(
      PusherSubscriptionEvent.SUBSCRIPTION_ERROR,
      this.handleSubscriptionError
    )

    // Bind to notification events
    this.channel.bind(NotificationEvent.CREATED, this.handleNotificationCreated)
    this.channel.bind(NotificationEvent.READ, this.handleNotificationRead)
    this.channel.bind(
      NotificationEvent.UNREAD_COUNT,
      this.handleUnreadCountUpdate
    )
  }

  private handleSubscriptionSucceeded = () => {
    this._isReady = true
    this.readyHandlers.forEach(handler => handler())
  }

  private handleSubscriptionError = (error: unknown) => {
    // eslint-disable-next-line no-console
    console.error(`Failed to subscribe to ${this.channelName}:`, error)
  }

  private handleNotificationCreated = (data: NotificationData) => {
    this.notificationHandlers.forEach(handler => handler(data))
  }

  private handleNotificationRead = (data: NotificationReadData) => {
    this.readHandlers.forEach(handler => handler(data))
  }

  private handleUnreadCountUpdate = (data: UnreadCountData) => {
    this.unreadCountHandlers.forEach(handler => handler(data))
  }

  /**
   * Register a handler for new notification events
   */
  onNotification(handler: NotificationEventHandler): () => void {
    this.notificationHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      this.notificationHandlers = this.notificationHandlers.filter(
        h => h !== handler
      )
    }
  }

  /**
   * Register a handler for notification read events
   */
  onNotificationRead(handler: NotificationReadHandler): () => void {
    this.readHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      this.readHandlers = this.readHandlers.filter(h => h !== handler)
    }
  }

  /**
   * Register a handler for unread count updates
   */
  onUnreadCountUpdate(handler: UnreadCountHandler): () => void {
    this.unreadCountHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      this.unreadCountHandlers = this.unreadCountHandlers.filter(
        h => h !== handler
      )
    }
  }

  /**
   * Register a handler for ready state changes
   */
  onReady(handler: () => void): () => void {
    this.readyHandlers.push(handler)

    // Return unsubscribe function
    return () => {
      this.readyHandlers = this.readyHandlers.filter(h => h !== handler)
    }
  }

  /**
   * Get whether Pusher real-time updates are enabled
   */
  get isPusherEnabled(): boolean {
    return this._isPusherEnabled
  }

  /**
   * Disconnect from Pusher and cleanup
   */
  disconnect(): void {
    if (this.pusher) {
      this.pusher.disconnect()
    }

    this.channel = null
    this.notificationHandlers = []
    this.readHandlers = []
    this.unreadCountHandlers = []
    this.readyHandlers = []
    this._isReady = false
  }
}
